/**
 * Browser-based utility to seed grades into Firestore
 *
 * Usage in browser console:
 * await seedGrades()
 *
 * This will seed the standard grade levels into Firestore
 */

import gradesService from '../services/gradesService.js';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase';

// Legacy grade options to seed into Firestore
const LEGACY_GRADE_OPTIONS = [
  { id: 'K', value: 'K', label: 'Kindergarten', order: 0 },
  { id: '1', value: '1', label: 'Grade 1', order: 1 },
  { id: '2', value: '2', label: 'Grade 2', order: 2 },
  { id: '3', value: '3', label: 'Grade 3', order: 3 },
  { id: '4', value: '4', label: 'Grade 4', order: 4 },
  { id: '5', value: '5', label: 'Grade 5', order: 5 },
  { id: '6', value: '6', label: 'Grade 6', order: 6 },
  { id: '7', value: '7', label: 'Grade 7', order: 7 },
  { id: '8', value: '8', label: 'Grade 8', order: 8 },
  { id: '9', value: '9', label: 'Grade 9', order: 9 },
  { id: '10', value: '10', label: 'Grade 10', order: 10 },
  { id: '11', value: '11', label: 'Grade 11', order: 11 },
  { id: '12', value: '12', label: 'Grade 12', order: 12 }
];

/**
 * Check if grades collection already has data
 */
async function checkExistingGrades() {
  const gradesRef = collection(db, 'grades');
  const snapshot = await getDocs(gradesRef);
  return snapshot.size;
}

/**
 * Seed grades into Firestore
 */
async function seedGrades() {
  try {
    console.log('🌱 Starting grades seeding process...');
    console.log('');

    // Check if data already exists
    const existingCount = await checkExistingGrades();
    if (existingCount > 0) {
      console.log(`⚠️  Found ${existingCount} existing grades in Firestore`);
      console.log('');
      const confirm = window.confirm(
        `The grades collection already has ${existingCount} grades.\n\n` +
        'Do you want to proceed anyway? This will add duplicates if grades with the same IDs exist.'
      );
      if (!confirm) {
        console.log('❌ Seeding cancelled by user');
        return { success: false, message: 'Cancelled by user' };
      }
    }

    console.log(`📝 Preparing to seed ${LEGACY_GRADE_OPTIONS.length} grades:`);
    LEGACY_GRADE_OPTIONS.forEach(grade => {
      console.log(`   - ${grade.label} (${grade.value})`);
    });
    console.log('');

    // Seed the data
    await gradesService.seedInitialData(LEGACY_GRADE_OPTIONS);

    console.log('✅ Seeding completed successfully!');
    console.log('Summary:');
    console.log(`   - ${LEGACY_GRADE_OPTIONS.length} grades added to Firestore`);
    console.log('   - Collection: grades');
    console.log('   - Grades ordered by: order field (0-12)');
    console.log('');

    // Verify the seeding
    console.log('🔍 Verifying seeding...');
    const finalCount = await checkExistingGrades();
    console.log(`✓ Found ${finalCount} grades in Firestore`);
    console.log('');

    console.log('🎉 Seeding complete! You can now:');
    console.log('   1. Use the GradesManager UI to manage grade levels');
    console.log('   2. Add new custom grade levels (e.g., Pre-K, Post-Grad)');
    console.log('   3. Edit or remove existing grade levels');

    return {
      success: true,
      message: 'Grades seeded successfully',
      count: LEGACY_GRADE_OPTIONS.length
    };

  } catch (error) {
    console.error('❌ Error seeding grades:', error);
    return {
      success: false,
      message: error.message,
      error
    };
  }
}

// Make available globally in browser console
window.seedGrades = seedGrades;

export default seedGrades;
